import React, { useState, useEffect } from 'react';
import { Phone, ChevronDown, MessageCircle, Send, Home, Flame, Search, Phone as PhoneIcon, Building2 } from 'lucide-react';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ['contacts', 'about', 'flights', 'hot-tours', 'tours', 'search', 'home'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom > 200) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mobileNavItems = [
    { href: '#home', icon: Home, label: 'Главная', id: 'home' },
    { href: '#search', icon: Search, label: 'Туры', id: 'search' },
    { href: '#hot-tours', icon: Flame, label: 'Горящие', id: 'hot-tours' },
    { href: '#about', icon: Building2, label: 'О нас', id: 'about' },
    { href: '#contacts', icon: PhoneIcon, label: 'Связь', id: 'contacts' },
  ];

  return (
    <>
      {/* Top Bar — desktop only */}
      <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-primary-800 text-white text-sm hidden lg:block ${isScrolled ? 'h-0 overflow-hidden opacity-0' : 'opacity-100'}`} style={{height: isScrolled ? 0 : 40}}>
        <div className="container-custom h-full flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="tel:+77019823126" className="flex items-center gap-1.5 hover:text-secondary-300 transition-colors">
              <Phone className="w-3.5 h-3.5" />
              <span>+7 701 982 31 26</span>
            </a>
            <span className="text-white/40">|</span>
            <span className="text-white/70">Пн-Пт: 11:00 - 19:00</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="https://wa.me/77019823126" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-green-400 transition-colors">
              <MessageCircle className="w-3.5 h-3.5" />
              <span>WhatsApp</span>
            </a>
            <a href="https://t.me/hasantravel" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-blue-400 transition-colors">
              <Send className="w-3.5 h-3.5" />
              <span>Telegram</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header — desktop */}
      <header
        className={`fixed left-0 right-0 z-40 transition-all duration-500 hidden lg:block ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-lg shadow-lg py-3'
            : 'bg-transparent py-4'
        }`}
        style={{top: isScrolled ? 0 : 40}}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                isScrolled ? 'bg-primary-500 text-white' : 'bg-white/20 backdrop-blur text-white'
              }`}>
                HT
              </div>
              <div>
                <span className={`text-xl font-bold block leading-tight transition-colors duration-300 ${isScrolled ? 'text-primary-500' : 'text-white'}`}>
                  HASAN TRAVEL
                </span>
                <span className={`text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${isScrolled ? 'text-neutral-400' : 'text-white/60'}`}>
                  Турагентство
                </span>
              </div>
            </a>

            <nav className="flex items-center space-x-1">
              <a href="#home" className={`navigation-link ${isScrolled ? 'text-neutral-700 hover:text-primary-500' : 'text-white'}`}>Главная</a>
              <div className="relative group">
                <button className={`navigation-link flex items-center ${isScrolled ? 'text-neutral-700 hover:text-primary-500' : 'text-white'}`}>
                  Направления
                  <ChevronDown className="ml-1 w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
                </button>
                <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-2xl bg-white ring-1 ring-black/5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 translate-y-2 group-hover:translate-y-0 overflow-hidden">
                  <div className="py-2">
                    {['Турция', 'Египет', 'ОАЭ', 'Таиланд', 'Мальдивы', 'Греция'].map((country) => (
                      <a key={country} href="#tours" className="block px-5 py-2.5 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-500 transition-colors">
                        {country}
                      </a>
                    ))}
                    <hr className="my-1 border-neutral-100" />
                    <a href="#tours" className="block px-5 py-2.5 text-sm font-medium text-primary-500 hover:bg-primary-50">Все направления</a>
                  </div>
                </div>
              </div>
              <a href="#hot-tours" className={`navigation-link ${isScrolled ? 'text-neutral-700 hover:text-primary-500' : 'text-white'}`}>Горящие туры</a>
              <a href="#about" className={`navigation-link ${isScrolled ? 'text-neutral-700 hover:text-primary-500' : 'text-white'}`}>О нас</a>
              <a href="#contacts" className={`navigation-link ${isScrolled ? 'text-neutral-700 hover:text-primary-500' : 'text-white'}`}>Контакты</a>
            </nav>

            <a href="#contacts" className="btn btn-secondary text-sm py-2.5">
              Заказать звонок
            </a>
          </div>
        </div>
      </header>

      {/* Mobile Header — simplified top bar */}
      <header className={`fixed top-0 left-0 right-0 z-40 lg:hidden transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-md' : 'bg-transparent'
      }`}>
        <div className="flex items-center justify-between px-4 py-3">
          <a href="/" className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
              isScrolled ? 'bg-primary-500 text-white' : 'bg-white/20 backdrop-blur text-white'
            }`}>
              HT
            </div>
            <span className={`text-lg font-bold ${isScrolled ? 'text-primary-500' : 'text-white'}`}>
              HASAN TRAVEL
            </span>
          </a>
          <a href="tel:+77019823126" className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
            isScrolled ? 'bg-primary-50 text-primary-500' : 'bg-white/20 text-white'
          }`}>
            <Phone className="w-4 h-4" />
          </a>
        </div>
      </header>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden px-4 pb-4 pt-0">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.12)] border border-neutral-200/50 px-2 py-2">
          <div className="flex items-center justify-around">
            {mobileNavItems.map((item) => {
              const isActive = activeSection === item.id ||
                (item.id === 'search' && activeSection === 'tours');
              return (
                <a
                  key={item.id}
                  href={item.href}
                  className={`flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-xl transition-all duration-300 min-w-[56px] ${
                    isActive
                      ? 'text-primary-500'
                      : 'text-neutral-400'
                  }`}
                >
                  <div className={`relative transition-all duration-300 ${isActive ? 'scale-110' : ''}`}>
                    {isActive && (
                      <div className="absolute -inset-2 bg-primary-500/10 rounded-xl" />
                    )}
                    <item.icon className={`w-5 h-5 relative z-10 ${isActive ? 'stroke-[2.5px]' : ''}`} />
                  </div>
                  <span className={`text-[10px] font-medium leading-tight ${isActive ? 'text-primary-500' : 'text-neutral-400'}`}>
                    {item.label}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;
